// src/components/ChatInput.js - VERSÃO FINAL COM MAPEAMENTO COMPLETO

import React, { useState, useRef, useEffect } from "react";
import { IoSendSharp } from "react-icons/io5";
import { FaMicrophone, FaTrashAlt } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import AudioPlayer from "./AudioPlayer";
import styles from "./ChatInput.module.css";

const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export default function ChatInput({ onSend, onSendAudio, theme, inputValue, onInputChange, enableAudio, audioData, setAudioData, isBotReplying }) {
    const [isRecording, setIsRecording] = useState(false);
    const [timer, setTimer] = useState(0);

    const textareaRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerIntervalRef = useRef(null);
    const streamRef = useRef(null);

    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, [inputValue]);

    const handleStartRecording = async () => {
        if (isRecording || isBotReplying) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream; 
            setAudioData(null);
            audioChunksRef.current = [];
            const recorder = new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) audioChunksRef.current.push(event.data);
            };
            recorder.start();
            setIsRecording(true);
            setTimer(0);
            timerIntervalRef.current = setInterval(() => setTimer(prev => prev + 1), 1000);
        } catch (err) {
            console.error("Erro ao acessar o microfone:", err);
            alert("Não foi possível acessar o microfone.");
        }
    };
    
    const handleStopAndPreview = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                const audioUrl = URL.createObjectURL(audioBlob);
                setAudioData({ blob: audioBlob, url: audioUrl });
                if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
                clearInterval(timerIntervalRef.current);
            };
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };
    
    const handleStopAndSend = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                onSendAudio(audioBlob);
                if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
                clearInterval(timerIntervalRef.current);
            };
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            setAudioData(null);
        }
    };

    const handleCancelPreview = () => setAudioData(null);

    const handleSend = () => {
        if (isBotReplying) return;
        if (inputValue.trim()) {
            onSend(inputValue.trim());
            onInputChange('');
        } else if (audioData) {
            onSendAudio(audioData.blob);
            setAudioData(null);
        }
    };
    
    const isSendDisabled = (!inputValue.trim() && !audioData) || isBotReplying;
    const containerClassName = `${styles['input-container']} ${isBotReplying ? styles['bot-is-typing'] : ''}`;

    if (isRecording) {
        return (
            <div className={containerClassName}>
                <div className={styles['recording-bar']}>
                    <div className={styles['recording-info']}>
                        <FaMicrophone className={styles['recording-mic-icon']} />
                        <span>{formatTime(timer)}</span>
                    </div>
                    <div className={styles['recording-actions']}>
                        <button className={styles['mic-btn']} title="Parar Gravação" onClick={handleStopAndPreview} disabled={isBotReplying}><FiX size={23} /></button>
                        <button className={styles['send-btn']} title="Enviar Gravação" onClick={handleStopAndSend} disabled={isBotReplying}>
                            <IoSendSharp size={23} color={theme.primary} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    if (audioData) {
        return (
            <div className={containerClassName}>
                <div className={`${styles['recording-bar']} ${styles['preview']}`}>
                    <AudioPlayer src={audioData.url} />
                    <div className={styles['recording-actions']}>
                        <button className={styles['mic-btn']} title="Descartar" onClick={handleCancelPreview} disabled={isBotReplying}><FaTrashAlt size={20} /></button>
                        <button className={styles['send-btn']} title="Enviar Áudio" onClick={handleSend} disabled={isBotReplying}>
                            <IoSendSharp size={23} color={theme.primary} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={containerClassName}>
            <textarea
                ref={textareaRef}
                className={styles['chat-text-input']}
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                placeholder="Digite sua pergunta"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }}}
                rows={1}
                disabled={isBotReplying}
            />
            <div className={styles['input-buttons-wrapper']}>
                {enableAudio && (
                    <button className={styles['mic-btn']} title="Gravar áudio" onClick={handleStartRecording} disabled={isBotReplying}>
                        <FaMicrophone size={20} />
                    </button>
                )}
                <button className={styles['send-btn']} title="Enviar" onClick={handleSend} disabled={isSendDisabled}>
                    <IoSendSharp size={23} color={theme.primary} />
                </button>
            </div>
        </div>
    );
}